/**
 * What DeepL Voice has heard so far, built from its WebSocket messages.
 *
 * DeepL sends a transcript in two kinds of segment: CONCLUDED ones are final
 * and arrive once, to be appended; TENTATIVE ones are its current guess at the
 * rest and replace the previous guess with every message. The translation
 * comes the same way, separately.
 */
export interface TranscriptPart {
    concluded: string[];
    tentative: string[];
}

export interface VoiceTranscript {
    source: TranscriptPart;
    /** Empty unless the session translates. */
    target: TranscriptPart;
    /**
     * The language DeepL named for each concluded segment it heard — what a
     * recording without a chosen language turned out to be.
     */
    heardLanguages: string[];
    /** DeepL has sent everything; the connection may close. */
    ended: boolean;
    /** DeepL's reason when it ended the session itself. */
    error: string | null;
}

export const EMPTY_TRANSCRIPT: VoiceTranscript = {
    source: { concluded: [], tentative: [] },
    target: { concluded: [], tentative: [] },
    heardLanguages: [],
    ended: false,
    error: null
};

interface Segment {
    text: string;
    /** BCP 47, on source segments only ('pl', 'pt-BR' …). */
    language?: string;
}

interface TranscriptUpdate {
    concluded?: Segment[];
    tentative?: Segment[];
}

/** The server messages this app reads; DeepL sends one key per message. */
export interface VoiceServerMessage {
    source_transcript_update?: TranscriptUpdate;
    target_transcript_update?: TranscriptUpdate & { language?: string };
    end_of_stream?: object;
    error?: { error_message?: string; reason_code?: string | number };
}

function merge(part: TranscriptPart, update: TranscriptUpdate): TranscriptPart {
    return {
        concluded: [
            ...part.concluded,
            ...(update.concluded ?? []).map((segment) => segment.text)
        ],
        tentative: (update.tentative ?? []).map((segment) => segment.text)
    };
}

export function applyVoiceMessage(
    transcript: VoiceTranscript,
    message: VoiceServerMessage
): VoiceTranscript {
    if (message.source_transcript_update) {
        const update = message.source_transcript_update;
        return {
            ...transcript,
            source: merge(transcript.source, update),
            heardLanguages: [
                ...transcript.heardLanguages,
                ...(update.concluded ?? []).flatMap((segment) =>
                    segment.language ? [segment.language] : []
                )
            ]
        };
    }
    if (message.target_transcript_update) {
        return {
            ...transcript,
            target: merge(transcript.target, message.target_transcript_update)
        };
    }
    if (message.end_of_stream) {
        return { ...transcript, ended: true };
    }
    if (message.error) {
        return {
            ...transcript,
            ended: true,
            error: String(
                message.error.error_message ??
                    message.error.reason_code ??
                    'error'
            )
        };
    }
    return transcript;
}

/**
 * The language most of the recording was in, as a bare lower-case code
 * ('pt', not 'pt-BR'), or null when DeepL named none. Most, not last: a
 * German place name in a Polish sentence must not make it German.
 */
export function dominantLanguage(transcript: VoiceTranscript): string | null {
    const counts = new Map<string, number>();
    for (const language of transcript.heardLanguages) {
        const base = language.toLowerCase().split('-')[0];
        counts.set(base, (counts.get(base) ?? 0) + 1);
    }
    let best: string | null = null;
    for (const [language, count] of counts) {
        if (best === null || count > (counts.get(best) ?? 0)) {
            best = language;
        }
    }
    return best;
}

/**
 * The text of a part. DeepL's segments carry their own spacing — a word comes
 * as " Grube", and a language without spaces between words has none — so
 * they are joined as they are, only squeezed and trimmed.
 * `withTentative` is for the live view; what goes into a field is only ever
 * what DeepL concluded — after the end, that is everything.
 */
export function transcriptText(
    part: TranscriptPart,
    withTentative = false
): string {
    return [...part.concluded, ...(withTentative ? part.tentative : [])]
        .join('')
        .replace(/\s+/g, ' ')
        .trim();
}
